import BasketReducerState from "./types/BasketReducerState";
import BasketReducerAction from "./types/BasketReducerAction";
import BasketReducerActions from "./enums/BasketReducerActions";
import BasketUpsale from "app/WebEvent/types/BasketUpsale";
import BasketTicket from "app/WebEvent/types/BasketTicket";
import getSubtranslation from "helpers/getSubtranslation";
import moment from "moment";
import { IWebTicket } from "app/WebEvent/interfaces";
import BookingUpsale from "app/WebEvent/types/BasketUpsale";

const basketReducer = (
    state: BasketReducerState,
    action: BasketReducerAction,
) => {
    switch (action.type) {
        case BasketReducerActions.CLEAR_EVENT: {
            return {
                ...state,
                event: null,
                tickets: [],
                upsales: [],
                totalBasePrice: 0,
                totalPrice: 0,
            };
        }

        case BasketReducerActions.SET_EVENT: {
            const format = "YYYY-MM-DD HH:mm:ss";
            const utcTime = moment.utc(action.payload.start_datetime, format);

            let tickets: BasketTicket[] = [];
            if (!action.payload.ticket_multiselection) {
                const availableTicket = action.payload.event_tickets.find(
                    (t) => t.current_available >= state.guests,
                );

                if (availableTicket) {
                    tickets = [prepareTicket(availableTicket, state.guests)];
                }
            }

            const newState = {
                ...state,
                event: {
                    ...action.payload,
                    description: getSubtranslation(
                        action.payload.subtranslations.description,
                    ),
                    title: getSubtranslation(
                        action.payload.subtranslations.title,
                    ),
                    location: getSubtranslation(
                        action.payload.subtranslations.location,
                    ),
                    localDateTime: moment(utcTime)
                        .local()
                        .locale(
                            document.documentElement.lang === "en"
                                ? "en-gb"
                                : document.documentElement.lang,
                        )
                        .format("dddd, DD.MM.YYYY, HH:mm"),
                },
                tickets,
                upsales: [],
            };

            const totals = getTotalPrice(newState);

            return {
                ...newState,
                groupUsage: calculateGroupUsage(newState),
                totalBasePrice: totals[0],
                totalPrice: totals[1],
                totalTickets: getTotalTickets(newState),
            };
        }

        case BasketReducerActions.SET_DISCOUNT_EVENT: {
            const format = "YYYY-MM-DD HH:mm:ss";
            const utcTime = moment.utc(action.payload.start_datetime, format);

            let tickets: BasketTicket[] = [];

            for (const ticket of state.tickets) {
                const matchingTicket = action.payload.event_tickets.find(
                    (t) => t.id === ticket.id,
                );

                if (matchingTicket) {
                    tickets.push({
                        ...ticket,
                        price: matchingTicket.price,
                        ticket_upsales: ticket.ticket_upsales.reduce(
                            (upsales, upsale) => {
                                const matchingUpsale =
                                    matchingTicket.upsales.find(
                                        (u) => u.id === upsale.id,
                                    );

                                if (matchingUpsale) {
                                    return [
                                        ...upsales,
                                        {
                                            ...upsale,
                                            price: matchingUpsale.price,
                                        },
                                    ];
                                }

                                return upsales;
                            },
                            [] as BookingUpsale[],
                        ),
                    });
                }
            }

            const newState = {
                ...state,
                event: {
                    ...action.payload,
                    description: getSubtranslation(
                        action.payload.subtranslations.description,
                    ),
                    title: getSubtranslation(
                        action.payload.subtranslations.title,
                    ),
                    location: getSubtranslation(
                        action.payload.subtranslations.location,
                    ),
                    localDateTime: moment(utcTime)
                        .local()
                        .locale(
                            document.documentElement.lang === "en"
                                ? "en-gb"
                                : document.documentElement.lang,
                        )
                        .format("dddd, DD.MM.YYYY, HH:mm"),
                },
                tickets,
            };

            const totals = getTotalPrice(newState);

            return {
                ...newState,
                groupUsage: calculateGroupUsage(newState),
                totalBasePrice: totals[0],
                totalPrice: totals[1],
                totalTickets: getTotalTickets(newState),
            };
        }

        case BasketReducerActions.SET_GUESTS: {
            let newState = {
                ...state,
                guests: action.payload,
                ...updateSingleTicketQuantity(state, action.payload),
            };

            //set quantity of first ticket to guests if multiselection is not allowed
            if (
                state.event &&
                !state.event.ticket_multiselection &&
                newState.tickets.length > 0
            ) {
                newState.tickets[0].quantity = action.payload;
            }

            const totals = getTotalPrice(newState);

            return {
                ...newState,
                groupUsage: calculateGroupUsage(newState),
                totalBasePrice: totals[0],
                totalPrice: totals[1],
                totalTickets: getTotalTickets(newState),
            };
        }

        case BasketReducerActions.SET_VIEW: {
            return {
                ...state,
                view: action.payload,
            };
        }

        case BasketReducerActions.INCREASE_GUESTS: {
            const newState = {
                ...state,
                guests: state.guests + 1,
                ...updateSingleTicketQuantity(state, state.guests + 1),
            };

            const totals = getTotalPrice(newState);

            return {
                ...newState,
                groupUsage: calculateGroupUsage(newState),
                totalBasePrice: totals[0],
                totalPrice: totals[1],
                totalTickets: getTotalTickets(newState),
            };
        }

        case BasketReducerActions.DECREASE_GUESTS: {
            const newState = {
                ...state,
                guests: state.guests - 1,
                ...updateSingleTicketQuantity(state, state.guests - 1),
            };

            const totals = getTotalPrice(newState);

            return {
                ...newState,
                groupUsage: calculateGroupUsage(newState),
                totalBasePrice: totals[0],
                totalPrice: totals[1],
                totalTickets: getTotalTickets(newState),
            };
        }

        case BasketReducerActions.CHANGE_UPSALE_QUANTITY: {
            let newState: BasketReducerState = { ...state };

            const upsaleIndex = state.upsales.findIndex(
                (u) => u.id === action.payload.upsale.id,
            );

            if (upsaleIndex === -1) {
                newState.upsales = [
                    ...state.upsales,
                    {
                        id: action.payload.upsale.id,
                        base_price: action.payload.upsale.base_price,
                        price: action.payload.upsale.price,
                        quantity: action.payload.quantity,
                    },
                ];
            } else {
                //remove upsale if quantity is 0
                if (action.payload.quantity === 0) {
                    newState.upsales = state.upsales.filter(
                        (u) => u.id !== action.payload.upsale.id,
                    );
                } else {
                    newState.upsales = state.upsales.map((upsale, index) => {
                        if (index === upsaleIndex) {
                            return {
                                ...upsale,
                                quantity: action.payload.quantity,
                            };
                        }

                        return upsale;
                    });
                }
            }

            const totals = getTotalPrice(newState);

            return {
                ...newState,
                groupUsage: calculateGroupUsage(newState),
                totalBasePrice: totals[0],
                totalPrice: totals[1],
                totalTickets: getTotalTickets(newState),
            };
        }

        case BasketReducerActions.CHANGE_TICKET_UPSALE_QUANTITY: {
            let newState: BasketReducerState = { ...state };

            const ticketIndex = state.tickets.findIndex(
                (t) => t.id === action.payload.ticket.id,
            );

            if (ticketIndex === -1) {
                return state;
            }

            const upsaleIndex = state.tickets[
                ticketIndex
            ].ticket_upsales.findIndex(
                (u) => u.id === action.payload.upsale.id,
            );

            if (upsaleIndex === -1) {
                newState.tickets = state.tickets.map((ticket, index) => {
                    if (index === ticketIndex) {
                        return {
                            ...ticket,
                            ticket_upsales: [
                                ...ticket.ticket_upsales,
                                {
                                    id: action.payload.upsale.id,
                                    base_price:
                                        action.payload.upsale.base_price,
                                    price: action.payload.upsale.price,
                                    quantity: action.payload.quantity,
                                },
                            ],
                        };
                    }

                    return ticket;
                });
            } else {
                //remove upsale if quantity is 0
                if (action.payload.quantity === 0) {
                    newState.tickets = state.tickets.map((ticket, index) => {
                        if (index === ticketIndex) {
                            return {
                                ...ticket,
                                ticket_upsales: ticket.ticket_upsales.filter(
                                    (u) => u.id !== action.payload.upsale.id,
                                ),
                            };
                        }

                        return ticket;
                    });
                } else {
                    newState.tickets = state.tickets.map((ticket, index) => {
                        if (index === ticketIndex) {
                            return {
                                ...ticket,
                                ticket_upsales: ticket.ticket_upsales.map(
                                    (upsale, upsaleIdx) => {
                                        if (upsaleIdx === upsaleIndex) {
                                            return {
                                                ...upsale,
                                                quantity:
                                                    action.payload.quantity,
                                            };
                                        }
                                        return upsale;
                                    },
                                ),
                            };
                        }
                        return ticket;
                    });
                }
            }

            const totals = getTotalPrice(newState);

            return {
                ...newState,
                groupUsage: calculateGroupUsage(newState),
                totalBasePrice: totals[0],
                totalPrice: totals[1],
                totalTickets: getTotalTickets(newState),
            };
        }

        case BasketReducerActions.CHANGE_TICKET_QUANTITY: {
            let newState: BasketReducerState = { ...state };

            const ticketIndex = state.tickets.findIndex(
                (t) => t.id === action.payload.ticket.id,
            );

            if (ticketIndex === -1) {
                if (action.payload.quantity > 0) {
                    newState.tickets = [
                        ...state.tickets,
                        prepareTicket(
                            action.payload.ticket,
                            action.payload.quantity,
                        ),
                    ];
                }
            } else {
                //remove ticket if quantity is 0
                if (action.payload.quantity === 0) {
                    newState.tickets = state.tickets.filter(
                        (t) => t.id !== action.payload.ticket.id,
                    );
                } else {
                    newState.tickets = state.tickets.map((ticket, index) => {
                        if (index === ticketIndex) {
                            return {
                                ...ticket,
                                quantity: action.payload.quantity,
                            };
                        }

                        return ticket;
                    });
                }
            }

            const totals = getTotalPrice(newState);

            return {
                ...newState,
                groupUsage: calculateGroupUsage(newState),
                totalBasePrice: totals[0],
                totalPrice: totals[1],
                totalTickets: getTotalTickets(newState),
            };
        }

        case BasketReducerActions.SET_PROMO_CODE: {
            return {
                ...state,
                promoCode: action.payload,
            };
        }

        case BasketReducerActions.SET_SINGLE_TICKET: {
            const newState = {
                ...state,
                tickets: [prepareTicket(action.payload, state.guests)],
            };

            const totals = getTotalPrice(newState);

            return {
                ...newState,
                groupUsage: calculateGroupUsage(newState),
                totalBasePrice: totals[0],
                totalPrice: totals[1],
                totalTickets: getTotalTickets(newState),
            };
        }

        default:
            return state;
    }
};

const getTotalPrice = (state: BasketReducerState) => {
    const totals = state.upsales.reduce(
        (acc: [number, number], item: BasketUpsale) => {
            acc[0] += item.base_price * (item.quantity || 1);
            acc[1] += item.price * (item.quantity || 1);
            return acc;
        },
        [0, 0] as [number, number],
    ) as [number, number];

    return state.tickets.reduce((acc: [number, number], val: BasketTicket) => {
        acc[0] +=
            val.base_price * val.quantity +
            val.ticket_upsales.reduce(
                (upsaleAcc, upsale) =>
                    upsaleAcc + upsale.base_price * upsale.quantity,
                0,
            ) *
                val.quantity;

        acc[1] +=
            val.price * val.quantity +
            val.ticket_upsales.reduce(
                (upsaleAcc, upsale) =>
                    upsaleAcc + upsale.price * upsale.quantity,
                0,
            ) *
                val.quantity;

        return acc;
    }, totals);
};

const getTotalTickets = (state: BasketReducerState) => {
    return state.tickets.reduce(
        (acc: number, val: BasketTicket) => acc + val.quantity,
        0,
    );
};

const updateSingleTicketQuantity = (
    state: BasketReducerState,
    quantity: number,
): Partial<BasketReducerState> => {
    //set quantity of first ticket to guests if multiselection is not allowed
    if (
        state.event &&
        !state.event.ticket_multiselection &&
        state.tickets.length === 1
    ) {
        const ticket = state.event.event_tickets.find(
            (t) => t.id === state.tickets[0].id,
        );

        if (ticket && ticket.current_available < quantity) {
            const availableTicket = state.event.event_tickets.find(
                (t) => t.current_available >= quantity,
            );

            if (availableTicket) {
                return { tickets: [prepareTicket(availableTicket, quantity)] };
            } else {
                return { tickets: [] };
            }
        } else {
            return { tickets: [{ ...state.tickets[0], quantity: quantity }] };
        }
    }

    return {};
};

const prepareTicket = (ticket: IWebTicket, quantity: number) => {
    return {
        id: ticket.id,
        event_ticket_group_id: ticket.event_ticket_group_id,
        base_price: ticket.base_price,
        price: ticket.price,
        quantity: quantity,
        ticket_upsales: ticket.upsales
            .filter((u) => u.mandatory || u.force)
            .map((u) => ({
                id: u.id,
                base_price: u.base_price,
                price: u.price,
                quantity: 1,
            })),
    };
};

const calculateGroupUsage = (state: BasketReducerState) => {
    const groupUsage: Record<number, number> = {};

    state.tickets.forEach((ticket) => {
        if (!groupUsage[ticket.event_ticket_group_id]) {
            groupUsage[ticket.event_ticket_group_id] = 0;
        }

        groupUsage[ticket.event_ticket_group_id] += ticket.quantity;
    });

    return groupUsage;
};

export default basketReducer;
